import React from "react";
import {useDispatch, useSelector} from "react-redux";
import {css, Theme, useTheme} from "@emotion/react";

import {elevation} from "@pg-design/elevation";
import {borderRadius, calculateRemSize, display, flexAbsoluteCenter, mb, mr, onDesktop, p, underline, w100} from "@pg-design/helpers-css";
import {PhoneIcon} from "@pg-design/icons-module";
import {getThemeBreakpoint} from "@pg-design/styles-strings";
import {Highlight, ITextProps, Text} from "@pg-design/text-module";
import {FormFieldType} from "@pg-mono/form/src/form_interfaces";
import {FormikForm, handleFormikPost, IFormikSubmitFn} from "@pg-mono/formik-utils";
import {rpAppLink} from "@pg-mono/rp-routes";

import {IRPStore} from "../../app/rp_reducer";
import {TermsAcceptation} from "../../application/components/TermsAcceptation";
import {toJsonValues} from "../../form/translate/to_json_values";
import {addNotification} from "../../notifications/state/notifications_slice";
import {ourOfferSuccessHit} from "../../tracking/algolytics_hits/our_offer_hit";
import {gtmGaEventOurOfferFormSuccess} from "../../tracking/google_tag_manager/gtm_our_offer_form_success";
import {ourOfferPost} from "../actions/our_form_post";
import {B2BUsercomSources, sendB2BUsercomEvent} from "../utils/send_b2b_usercom_event";

const initialValues = {
    name: "",
    email: "",
    company: "",
    phone: {
        code: 48,
        number: ""
    },
    text: "Potrzebuję wsparcia przy promocji moich inwestycji. Proszę o kontakt."
};

export const ourOfferFormFields = {
    name: FormFieldType.Input,
    email: FormFieldType.Input,
    company: FormFieldType.Input,
    phone: FormFieldType.PhoneField,
    text: FormFieldType.Input
};

export interface IOurOfferFormValues {
    name: string;
    email: string;
    company: string;
    phone: string;
    text: string;
}

interface IProps {
    smallVersion?: boolean;
}

export const ContactForm = (props: IProps) => {
    const theme = useTheme();
    const brandColor = theme.colors.primary;

    const codes = useSelector((store: IRPStore) => store.variables.data?.country_codes);
    const dispatch = useDispatch();

    const onSubmit: IFormikSubmitFn<typeof initialValues> = async (values, formikHelpers) => {
        const parsedValues = toJsonValues(ourOfferFormFields, values) as IOurOfferFormValues;

        handleFormikPost(ourOfferPost(parsedValues), formikHelpers, {
            onSuccess: () => {
                // hit success - algolytics
                ourOfferSuccessHit(parsedValues);
                // hit success - gtm
                gtmGaEventOurOfferFormSuccess();
                sendB2BUsercomEvent({
                    ...parsedValues,
                    source: props.smallVersion ? B2BUsercomSources.OUR_OFFER_FORM_TOP : B2BUsercomSources.OUR_OFFER_FORM
                });
                dispatch(addNotification({type: "success", content: "Twoje zgłoszenie zostało wysłane."}));
                formikHelpers.resetForm();
            },
            onNonFieldError: (nonFieldErrors) => {
                dispatch(addNotification({type: "warning", content: nonFieldErrors}));
            }
        });
    };

    const formHeadlineProps: Partial<ITextProps> = {
        variant: props.smallVersion ? "headline_2" : "headline_1",
        as: "h2",
        align: "center",
        mb: props.smallVersion ? "2.4rem" : "3.2rem"
    };

    return (
        <div css={props.smallVersion ? smallCard : contactCard}>
            <FormikForm onSubmit={onSubmit} initialValues={initialValues}>
                <div css={[display(props.smallVersion ? "block" : "none"), onDesktop(display("block"))]}>
                    <Text {...formHeadlineProps}>
                        {props.smallVersion ? (
                            <>
                                Współpracuj z nami już od <Highlight> 299 zł</Highlight> miesięcznie
                            </>
                        ) : (
                            <>
                                Porozmawiajmy
                                <br /> o Twojej inwestycji
                            </>
                        )}
                    </Text>

                    <Text as="p" mb="3.2rem" variant="body_copy_1" align="center">
                        Skontaktuj się z nami. Nasz konsultant przygotuje dla Ciebie ofertę dopasowaną do skali Twojej
                        <br css={props.smallVersion && display("none")} /> firmy i jej celów marketingowych.
                    </Text>

                    {!props.smallVersion && (
                        <div css={[flexAbsoluteCenter, mb(6)]}>
                            <PhoneIcon css={mr(2)} wrapperColor={brandColor} wrapperSize="4.8" size="3" />
                            <Text variant="headline_3" as="span">
                                +48 22 825 60 71
                            </Text>
                        </div>
                    )}
                </div>

                <FormikForm.Input name="name" css={mb(2)} placeholder="Imię i nazwisko" />
                <FormikForm.Input name="email" type="email" css={mb(2)} placeholder="Email" />
                <FormikForm.PhoneField codes={codes ?? []} name="phone" css={mb(2)} placeholder="Numer telefonu" className="bt" />
                <FormikForm.Input name="company" css={mb(2)} placeholder="Firma" />
                <FormikForm.Textarea name="text" css={[mb(2)]} placeholder="Wiadomość" />

                <Text css={rodoText} variant="info_txt_1">
                    Administratorem danych osobowych jest Property Group sp. z o.o.
                    <br />
                    <a css={link} href={rpAppLink.rodoTerms.base()} target="_blank">
                        Kliknij, aby dowiedzieć się m.in., w jakim celu przetwarzamy Twoje dane oraz jakie przysługują Ci prawa.
                    </a>
                </Text>

                <TermsAcceptation css={mb(3)} isPartnerTerms />

                <FormikForm.Button variant="filled_primary" css={w100}>
                    Wyślij
                </FormikForm.Button>
            </FormikForm>
        </div>
    );
};

const link = css`
    color: inherit;
    ${underline};
`;

const contactCard = css`
    display: inline-block;
    background-color: #fff;
    ${borderRadius(2)};
    ${elevation(2)};
    ${p(4, 2, 5, 2)};

    @media (min-width: ${getThemeBreakpoint().md}) {
        ${p(6, 12, 7, 12)};
    }
`;

const smallCard = css`
    max-width: 552px;
    background-color: #fff;
    ${borderRadius(2)};
    ${elevation(4)};
    ${p(2)};

    @media (min-width: ${getThemeBreakpoint().md}) {
        ${p(3)};
    }
`;

const rodoText = (theme: Theme) => css`
    color: ${theme.colors.gray[700]};
    margin-bottom: ${calculateRemSize(2)};
    text-align: center;
`;
import React from "react";
import {useDispatch, useSelector} from "react-redux";
import {css, Theme, useTheme} from "@emotion/react";

import {elevation} from "@pg-design/elevation";
import {borderRadius, calculateRemSize, display, flexAbsoluteCenter, mb, mr, onDesktop, p, underline, w100} from "@pg-design/helpers-css";
import {PhoneIcon} from "@pg-design/icons-module";
import {getThemeBreakpoint} from "@pg-design/styles-strings";
import {Highlight, ITextProps, Text} from "@pg-design/text-module";
import {FormFieldType} from "@pg-mono/form/src/form_interfaces";
import {FormikForm, handleFormikPost, IFormikSubmitFn} from "@pg-mono/formik-utils";
import {rpAppLink} from "@pg-mono/rp-routes";

import {IRPStore} from "../../app/rp_reducer";
import {TermsAcceptation} from "../../application/components/TermsAcceptation";
import {toJsonValues} from "../../form/translate/to_json_values";
import {addNotification} from "../../notifications/state/notifications_slice";
import {ourOfferSuccessHit} from "../../tracking/algolytics_hits/our_offer_hit";
import {gtmGaEventOurOfferFormSuccess} from "../../tracking/google_tag_manager/gtm_our_offer_form_success";
import {ourOfferPost} from "../actions/our_form_post";
import {B2BUsercomSources, sendB2BUsercomEvent} from "../utils/send_b2b_usercom_event";

const initialValues = {
    name: "",
    email: "",
    company: "",
    phone: {
        code: 48,
        number: ""
    },
    text: "Potrzebuję wsparcia przy promocji moich inwestycji. Proszę o kontakt."
};

export const ourOfferFormFields = {
    name: FormFieldType.Input,
    email: FormFieldType.Input,
    company: FormFieldType.Input,
    phone: FormFieldType.PhoneField,
    text: FormFieldType.Input
};

export interface IOurOfferFormValues {
    name: string;
    email: string;
    company: string;
    phone: string;
    text: string;
}

interface IProps {
    smallVersion?: boolean;
}

export const ContactForm = (props: IProps) => {
    const theme = useTheme();
    const brandColor = theme.colors.primary;

    const codes = useSelector((store: IRPStore) => store.variables.data?.country_codes);
    const dispatch = useDispatch();

    const onSubmit: IFormikSubmitFn<typeof initialValues> = async (values, formikHelpers) => {
        const parsedValues = toJsonValues(ourOfferFormFields, values) as IOurOfferFormValues;

        handleFormikPost(ourOfferPost(parsedValues), formikHelpers, {
            onSuccess: () => {
                // hit success - algolytics
                ourOfferSuccessHit(parsedValues);
                // hit success - gtm
                gtmGaEventOurOfferFormSuccess();
                sendB2BUsercomEvent({
                    ...parsedValues,
                    source: props.smallVersion ? B2BUsercomSources.OUR_OFFER_FORM_TOP : B2BUsercomSources.OUR_OFFER_FORM
                });
                dispatch(addNotification({type: "success", content: "Twoje zgłoszenie zostało wysłane."}));
                formikHelpers.resetForm();
            },
            onNonFieldError: (nonFieldErrors) => {
                dispatch(addNotification({type: "warning", content: nonFieldErrors}));
            }
        });
    };

    const formHeadlineProps: Partial<ITextProps> = {
        variant: props.smallVersion ? "headline_2" : "headline_1",
        as: "h2",
        align: "center",
        mb: props.smallVersion ? "2.4rem" : "3.2rem"
    };

    return (
        <div css={props.smallVersion ? smallCard : contactCard}>
            <FormikForm onSubmit={onSubmit} initialValues={initialValues}>
                <div css={[display(props.smallVersion ? "block" : "none"), onDesktop(display("block"))]}>
                    <Text {...formHeadlineProps}>
                        {props.smallVersion ? (
                            <>
                                Współpracuj z nami już od <Highlight> 299 zł</Highlight> miesięcznie
                            </>
                        ) : (
                            <>
                                Porozmawiajmy
                                <br /> o Twojej inwestycji
                            </>
                        )}
                    </Text>

                    <Text as="p" mb="3.2rem" variant="body_copy_1" align="center">
                        Skontaktuj się z nami. Nasz konsultant przygotuje dla Ciebie ofertę dopasowaną do skali Twojej
                        <br css={props.smallVersion && display("none")} /> firmy i jej celów marketingowych.
                    </Text>

                    {!props.smallVersion && (
                        <div css={[flexAbsoluteCenter, mb(6)]}>
                            <PhoneIcon css={mr(2)} wrapperColor={brandColor} wrapperSize="4.8" size="3" />
                            <Text variant="headline_3" as="span">
                                +48 22 825 60 71
                            </Text>
                        </div>
                    )}
                </div>

                <FormikForm.Input name="name" css={mb(2)} placeholder="Imię i nazwisko" />
                <FormikForm.Input name="email" type="email" css={mb(2)} placeholder="Email" />
                <FormikForm.PhoneField codes={codes ?? []} name="phone" css={mb(2)} placeholder="Numer telefonu" className="bt" />
                <FormikForm.Input name="company" css={mb(2)} placeholder="Firma" />
                <FormikForm.Textarea name="text" css={[mb(2)]} placeholder="Wiadomość" />

                <Text css={rodoText} variant="info_txt_1">
                    Administratorem danych osobowych jest Property Group sp. z o.o.
                    <br />
                    <a css={link} href={rpAppLink.rodoTerms.base()} target="_blank">
                        Kliknij, aby dowiedzieć się m.in., w jakim celu przetwarzamy Twoje dane oraz jakie przysługują Ci prawa.
                    </a>
                </Text>

                <TermsAcceptation css={mb(3)} isPartnerTerms />

                <FormikForm.Button variant="filled_primary" css={w100}>
                    Wyślij
                </FormikForm.Button>
            </FormikForm>
        </div>
    );
};

const link = css`
    color: inherit;
    ${underline};
`;

const contactCard = css`
    display: inline-block;
    background-color: #fff;
    ${borderRadius(2)};
    ${elevation(2)};
    ${p(4, 2, 5, 2)};

    @media (min-width: ${getThemeBreakpoint().md}) {
        ${p(6, 12, 7, 12)};
    }
`;

const smallCard = css`
    max-width: 552px;
    background-color: #fff;
    ${borderRadius(2)};
    ${elevation(4)};
    ${p(2)};

    @media (min-width: ${getThemeBreakpoint().md}) {
        ${p(3)};
    }
`;

const rodoText = (theme: Theme) => css`
    color: ${theme.colors.gray[700]};
    margin-bottom: ${calculateRemSize(2)};
    text-align: center;
`;
